import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { jsx, jsxs } from 'react/jsx-runtime.js';
import Popup from '@jetbrains/ring-ui/dist/popup/popup.js';
import cn from 'classnames';
import { useState, useRef, useContext, useEffect, useCallback } from 'react';
import FocusLock from 'react-focus-lock';
import { MODAL_ANIMATION_DURATION } from '../../config/globalConstants.js';
import { useIsMountedRef } from '../../hooks/useIsMountedRef.js';
import { useNoscroll } from '../../hooks/useNoscroll.js';
import '../../services/alert/alert.js';
import { FocusRestoreContext } from '../../services/focusRestore/FocusRestoreContext.js';
import { Header } from './Header/Header.js';
import styles from './ModalWrapper.module.css.js';

var DEFAULT_WIDTH = 670;
var DEFAULT_HEIGHT = 500;
var stopPropagationHandler = function stopPropagationHandler(e) {
  e.stopPropagation();
};
var ModalWrapper = function ModalWrapper(props) {
  var _cn, _cn2;
  var _props$className = props.className,
    className = _props$className === void 0 ? '' : _props$className,
    _props$keyboard = props.keyboard,
    keyboard = _props$keyboard === void 0 ? true : _props$keyboard,
    _props$maskClosable = props.maskClosable,
    maskClosable = _props$maskClosable === void 0 ? true : _props$maskClosable,
    onClose = props.onClose,
    _props$closeButtonDis = props.closeButtonDisabled,
    closeButtonDisabled = _props$closeButtonDis === void 0 ? false : _props$closeButtonDis,
    opened = props.opened,
    _props$title = props.title,
    title = _props$title === void 0 ? null : _props$title,
    titleClassName = props.titleClassName,
    titleTextClassName = props.titleTextClassName,
    popupContentClassName = props.popupContentClassName,
    _props$width = props.width,
    width = _props$width === void 0 ? DEFAULT_WIDTH : _props$width,
    _props$height = props.height,
    height = _props$height === void 0 ? DEFAULT_HEIGHT : _props$height;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    showContent = _useState2[0],
    setShowContent = _useState2[1];
  var timeoutRef = useRef();
  var isMountedRef = useIsMountedRef();
  var previousFocusedElement = useContext(FocusRestoreContext);
  useEffect(function () {
    return function () {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, [opened]);
  var close = useCallback(function () {
    if (!onClose || timeoutRef.current) {
      return;
    }
    setShowContent(false);
    timeoutRef.current = setTimeout(function () {
      onClose();
      if (isMountedRef.current) {
        setShowContent(true);
      }
    }, MODAL_ANIMATION_DURATION);
  }, [isMountedRef, onClose]);
  var onOutsideClick = useCallback(function (e) {
    var id = e.target.getAttribute('id');
    if (id === "qd_modal_".concat(styles.container)) {
      close();
    }
  }, [close]);
  var handleFocusLockDeactivation = useCallback(function () {
    if (typeof previousFocusedElement === 'string') {
      var _document$getElementB;
      (_document$getElementB = document.getElementById(previousFocusedElement)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.focus();
    } else {
      previousFocusedElement === null || previousFocusedElement === void 0 ? void 0 : previousFocusedElement.focus();
    }
  }, [previousFocusedElement]);
  useNoscroll(document.body, opened);
  // it is important for teamcity extension
  useNoscroll(document.body.parentElement || undefined, opened);
  if (!opened) {
    return null;
  }
  return jsx("div", {
    id: "qd_modal_".concat(styles.container),
    className: cn(styles.container, (_cn = {}, _defineProperty(_cn, styles.opened, showContent), _defineProperty(_cn, styles.closed, !showContent), _cn)),
    onClick: stopPropagationHandler,
    "aria-hidden": true,
    children: jsx("div", {
      style: {
        width: width
      },
      className: styles.content,
      children: jsx(Popup, {
        className: cn(styles.popup, (_cn2 = {}, _defineProperty(_cn2, styles.opened, showContent), _defineProperty(_cn2, styles.closed, !showContent), _cn2), className),
        style: {
          width: width,
          height: height
        },
        "data-test": props['data-qd-e2e-selector'],
        onEscPress: keyboard ? close : undefined,
        onOutsideClick: maskClosable ? onOutsideClick : undefined,
        children: jsxs(FocusLock, {
          returnFocus: true,
          onDeactivation: handleFocusLockDeactivation,
          children: [!!title && jsx(Header, {
            "data-qd-test": props['data-qd-e2e-selector'],
            className: titleClassName,
            titleTextClassName: titleTextClassName,
            onClick: onClose && close,
            closeButtonDisabled: closeButtonDisabled,
            children: title
          }), jsx("div", {
            className: cn(styles.popupContent, popupContentClassName),
            children: props.children
          })]
        })
      })
    })
  });
};

export { ModalWrapper };
